/** Перенос `domain/models/tablet_config_model/tablet_config_model.dart`. */

import { z } from 'zod';

import {
  sttConfigModelSchema,
  sttConfigModelToJson,
  type SttConfigModel,
} from '@/domain/models/sttConfigModel';

/** Тип персонажа по умолчанию: старый бекенд поле не отдаёт. */
const DEFAULT_CHARACTER_TYPE = 'web';

/**
 * Ответ `GET /api/v1/tablets/config` (по токену планшета).
 *
 * Единственное, что отсюда нужно приложению, — персонажи с полными STT
 * (`api_key` есть только здесь). Матчинг с `persons[]` открытой конфигурации
 * делается по `slug`: `id` здесь UUID, а в `persons[]` — целое число.
 */
export interface TabletConfigModel {
  readonly nodeName: string | null;
  readonly nodeAddress: string | null;
  readonly characters: readonly TabletCharacterModel[];
}

/** Элемент `characters[]` защищённой конфигурации. */
export interface TabletCharacterModel {
  /** UUID персонажа — с `persons[].id` (int) не совпадает. */
  readonly id: string;
  readonly slug: string;
  readonly characterType: string;
  readonly stt: SttConfigModel | null;
}

/** snake_case из ответа разбирается «как есть», `transform` даёт camelCase. */
const tabletCharacterModelRawSchema = z.object({
  id: z.string(),
  slug: z.string(),
  character_type: z.string().nullish(),
  stt: sttConfigModelSchema.nullish(),
});

export const tabletCharacterModelSchema = tabletCharacterModelRawSchema.transform(
  (raw): TabletCharacterModel => ({
    id: raw.id,
    slug: raw.slug,
    characterType: raw.character_type ?? DEFAULT_CHARACTER_TYPE,
    stt: raw.stt ?? null,
  }),
);

const tabletConfigModelRawSchema = z.object({
  node_name: z.string().nullish(),
  node_address: z.string().nullish(),
  characters: z.array(tabletCharacterModelSchema).nullish(),
});

export const tabletConfigModelSchema = tabletConfigModelRawSchema.transform(
  (raw): TabletConfigModel => ({
    nodeName: raw.node_name ?? null,
    nodeAddress: raw.node_address ?? null,
    characters: raw.characters ?? [],
  }),
);

export function tabletConfigModelFromJson(json: unknown): TabletConfigModel {
  return tabletConfigModelSchema.parse(json);
}

export function tabletCharacterModelFromJson(json: unknown): TabletCharacterModel {
  return tabletCharacterModelSchema.parse(json);
}

export function tabletCharacterModelToJson(model: TabletCharacterModel): Record<string, unknown> {
  return {
    id: model.id,
    slug: model.slug,
    character_type: model.characterType,
    stt: model.stt === null ? null : sttConfigModelToJson(model.stt),
  };
}

export function tabletConfigModelToJson(model: TabletConfigModel): Record<string, unknown> {
  return {
    node_name: model.nodeName,
    node_address: model.nodeAddress,
    characters: model.characters.map((character) => tabletCharacterModelToJson(character)),
  };
}
